import React, { useState, useEffect } from 'react';
import { stringingApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

interface StringingManagementProps {
  facilityId: string;
  businessId: string;
}

type OrderStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'pending_payment';

const StringingManagement: React.FC<StringingManagementProps> = ({ facilityId, businessId }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await stringingApi.getAllOrders(businessId, facilityId);
      setOrders(response);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [facilityId, user, businessId]);

  const handleStatusUpdate = async (orderId: string, newStatus: OrderStatus) => {
    try {
      await stringingApi.updateOrderStatus(businessId, facilityId, orderId, newStatus);
      await fetchOrders(); // Refresh the orders list
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status');
    }
  };

  // Filter orders into three groups
  const paidPendingOrders = orders.filter(
    (order) => order.status === 'pending' && order.paymentStatus === 'paid'
  );
  const inProgressOrders = orders.filter(
    (order) => order.status === 'in_progress'
  );
  const completedOrders = orders.filter(
    (order) => order.status === 'completed'
  );
  const pendingPaymentOrders = orders.filter(
    (order) => order.status === 'pending_payment'
  );

  const renderOrdersTable = (ordersToShow: any[], showStart: boolean, showComplete: boolean) => (
    <table className="min-w-full divide-y divide-gray-200 mb-8">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Racquet</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">String Pattern</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strings</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tension</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {ordersToShow.map((order) => (
          <tr key={order.id}>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {format(new Date(order.createdAt), 'MMM d, yyyy')}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {order.customerDetails?.name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {order.racquetDetails?.brand} {order.racquetDetails?.model}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {order.racquetDetails?.stringPattern}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {order.stringDetails?.mains} / {order.stringDetails?.crosses}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {order.racquetDetails?.tension.mains} / {order.racquetDetails?.tension.crosses} lbs
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                order.status === 'completed' ? 'bg-green-100 text-green-800' :
                order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                order.status === 'pending_payment' ? 'bg-orange-100 text-orange-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {order.status.replace('_', ' ')}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {order.paymentStatus === 'paid' ? (
                <span className="text-green-600 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-1" />
                  Paid
                </span>
              ) : (
                <span className="text-red-600 flex items-center">
                  <XCircleIcon className="h-5 w-5 mr-1" />
                  Unpaid
                </span>
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <div className="flex space-x-2">
                {showStart && (
                  <button
                    onClick={() => handleStatusUpdate(order.id, 'in_progress')}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    Start
                  </button>
                )}
                {showComplete && (
                  <button
                    onClick={() => handleStatusUpdate(order.id, 'completed')}
                    className="text-green-600 hover:text-green-900"
                  >
                    Complete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <XCircleIcon className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Stringing Orders</h2>
      <div className="mb-10">
        <h3 className="text-xl font-semibold mb-2">Pending Payment</h3>
        {pendingPaymentOrders.length > 0 ? (
          renderOrdersTable(pendingPaymentOrders, false, false)
        ) : (
          <div className="text-gray-500 mb-6">No pending payment orders.</div>
        )}
      </div>
      <div className="mb-10">
        <h3 className="text-xl font-semibold mb-2">Paid + Pending Jobs</h3>
        {paidPendingOrders.length > 0 ? (
          renderOrdersTable(paidPendingOrders, true, false)
        ) : (
          <div className="text-gray-500 mb-6">No paid + pending jobs.</div>
        )}
      </div>
      <div className="mb-10">
        <h3 className="text-xl font-semibold mb-2">Currently In Progress</h3>
        {inProgressOrders.length > 0 ? (
          renderOrdersTable(inProgressOrders, false, true)
        ) : (
          <div className="text-gray-500 mb-6">No jobs in progress.</div>
        )}
      </div>
      <div className="mb-10">
        <h3 className="text-xl font-semibold mb-2">Completed</h3>
        {completedOrders.length > 0 ? (
          renderOrdersTable(completedOrders, false, false)
        ) : (
          <div className="text-gray-500 mb-6">No completed jobs.</div>
        )}
      </div>
    </div>
  );
};

export default StringingManagement; 